import type { BlogPost, BlogPostMeta } from "./types";

function parseFrontmatter(raw: string): { meta: Record<string, unknown>; body: string } {
  const normalized = raw.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n");
  const frontmatter = normalized.match(/^---\n([\s\S]*?)\n---(?:\n|$)/);
  if (!frontmatter) {
    return { meta: {}, body: normalized };
  }
  const fm = frontmatter[1] ?? "";
  const body = normalized.slice(frontmatter[0].length).replace(/^\n+/, "");
  const meta: Record<string, unknown> = {};
  let listKey: string | null = null;
  for (const line of fm.split("\n")) {
    if (/^\s+-\s+/.test(line) && listKey) {
      const item = line.replace(/^\s+-\s+/, "").replace(/^["']|["']$/g, "").trim();
      const arr = (meta[listKey] as string[]) ?? [];
      arr.push(item);
      meta[listKey] = arr;
      continue;
    }
    listKey = null;
    const m = line.match(/^([A-Za-z0-9_]+):\s*(.*)$/);
    if (!m) continue;
    const key = m[1]!;
    let val = m[2]!.trim();
    if (val === "" || val === "|" || val === ">") {
      listKey = key;
      meta[key] = meta[key] ?? [];
      continue;
    }
    if (
      (val.startsWith('"') && val.endsWith('"')) ||
      (val.startsWith("'") && val.endsWith("'"))
    ) {
      val = val.slice(1, -1);
    }
    meta[key] = val;
  }
  return { meta, body };
}

/** Minimal markdown → HTML for blog bodies (headings, lists, p, links, tables, hr, strong/em). */
export function markdownToHtml(md: string): string {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let i = 0;
  let inUl = false;
  let inTable = false;

  const closeUl = () => {
    if (inUl) {
      out.push("</ul>");
      inUl = false;
    }
  };
  const closeTable = () => {
    if (inTable) {
      out.push("</tbody></table>");
      inTable = false;
    }
  };

  const inline = (s: string) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(
        /\[([^\]]+)\]\((https?:[^)]+)\)/g,
        '<a href="$2" rel="noopener noreferrer">$1</a>'
      );

  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (line.trim() === "") {
      closeUl();
      closeTable();
      i += 1;
      continue;
    }
    if (line.startsWith("# ")) {
      closeUl();
      closeTable();
      out.push(`<h1>${inline(line.slice(2))}</h1>`);
      i += 1;
      continue;
    }
    if (line.startsWith("## ")) {
      closeUl();
      closeTable();
      out.push(`<h2>${inline(line.slice(3))}</h2>`);
      i += 1;
      continue;
    }
    if (line.startsWith("### ")) {
      closeUl();
      closeTable();
      out.push(`<h3>${inline(line.slice(4))}</h3>`);
      i += 1;
      continue;
    }
    if (line.trim() === "---") {
      closeUl();
      closeTable();
      out.push("<hr />");
      i += 1;
      continue;
    }
    if (line.trim().startsWith("|") && line.includes("|")) {
      closeUl();
      const cells = line
        .split("|")
        .slice(1, -1)
        .map((c) => c.trim());
      const next = lines[i + 1] ?? "";
      if (/^\|?\s*[-:| ]+\s*\|?/.test(next)) {
        closeTable();
        out.push("<table><thead><tr>");
        for (const c of cells) out.push(`<th>${inline(c)}</th>`);
        out.push("</tr></thead><tbody>");
        inTable = true;
        i += 2;
        continue;
      }
      if (inTable) {
        out.push("<tr>");
        for (const c of cells) out.push(`<td>${inline(c)}</td>`);
        out.push("</tr>");
        i += 1;
        continue;
      }
    }
    if (line.trim().startsWith("- ") || line.trim().startsWith("* ")) {
      closeTable();
      if (!inUl) {
        out.push("<ul>");
        inUl = true;
      }
      out.push(`<li>${inline(line.trim().slice(2))}</li>`);
      i += 1;
      continue;
    }
    if (/^\d+\.\s/.test(line.trim())) {
      closeTable();
      closeUl();
      // treat ordered as paragraphs with number for simplicity
      out.push(`<p>${inline(line.trim())}</p>`);
      i += 1;
      continue;
    }
    closeUl();
    closeTable();
    out.push(`<p>${inline(line)}</p>`);
    i += 1;
  }
  closeUl();
  closeTable();
  return out.join("\n");
}

export function parseBlogMarkdown(raw: string, fileHint?: string): BlogPost {
  const { meta, body } = parseFrontmatter(raw);
  const keywords = Array.isArray(meta.keywords)
    ? (meta.keywords as string[])
    : typeof meta.keywords === "string"
      ? [meta.keywords]
      : [];
  const slug =
    String(meta.slug ?? fileHint ?? "post")
      .replace(/\.md$/, "")
      .replace(/^\d{4}-\d{2}-\d{2}-/, "") || "post";
  const postMeta: BlogPostMeta = {
    title: String(meta.title ?? slug),
    description: String(meta.description ?? ""),
    slug,
    date: String(meta.date ?? "1970-01-01"),
    locale: String(meta.locale ?? "sv"),
    keywords,
    cluster: String(meta.cluster ?? "general"),
    canonical:
      typeof meta.canonical === "string"
        ? meta.canonical
        : `https://andrum.app/blog/${slug}`,
  };
  // Strip first H1 from body if it duplicates title (we render title in page chrome)
  let bodyMd = body;
  const firstLine = bodyMd.split("\n")[0] ?? "";
  if (firstLine.startsWith("# ")) {
    bodyMd = bodyMd.slice(firstLine.length).replace(/^\n+/, "");
  }
  return {
    ...postMeta,
    body: bodyMd,
    html: markdownToHtml(bodyMd),
  };
}
